// @seventide/types — platform area: billing (FR-13.1, FR-13.2, FR-13.3, AN-2 churn reasons).
// Re-exported by ../platform.ts. Prices live HERE (integer pence, one source for web + API): the API sends them
// to Stripe as inline prices, so what the app shows is what Stripe charges (VAT-inclusive). There are no Stripe
// price ids to configure.
import { z } from 'zod';
import { Id, IsoDate, IsoDateTime } from '../common';
import { Nation, Stage, SubscriptionStatus } from '../enums';

// ---------------------------------------------------------------- plans (FR-13.1)

export const BILLING_PLANS = ['family_monthly', 'family_annual', 'archive'] as const;
export const BillingPlanId = z.enum(BILLING_PLANS);
export type BillingPlanId = z.infer<typeof BillingPlanId>;

export type BillingInterval = 'month' | 'year';

export interface BillingPlanInfo {
  id: BillingPlanId;
  /** Short plain-English name. */
  name: string;
  /** Price per interval in integer pence, VAT included (FR-3.6). Charged as-is through Stripe. */
  pricePence: number;
  interval: BillingInterval;
  /** One-line description shown on the plan card. */
  description: string;
  /** Archive plan is only offered once the workspace is in the estate stage (FR-10.2). */
  estateOnly: boolean;
}

/** FR-13.1: up to 5 members (the owner counts; the Principal's own membership does not) and 1 Principal. */
export const BILLING_MEMBER_LIMIT = 5;
export const BILLING_PRINCIPAL_LIMIT = 1;
/** Shown when the member limit is reached (API 409 `member_limit`; the DB backstop raises the same text). */
export const MEMBER_LIMIT_MESSAGE =
  'A family workspace can have up to 5 members, including the owner (the Principal does not count). Remove a member or cancel a pending invitation first.';

export const BILLING_PLAN_INFO: Record<BillingPlanId, BillingPlanInfo> = {
  family_monthly: {
    id: 'family_monthly',
    name: 'Family plan, paid monthly',
    pricePence: 599,
    interval: 'month',
    description: `Everything in Seventide for up to ${BILLING_MEMBER_LIMIT} family members and ${BILLING_PRINCIPAL_LIMIT} Principal.`,
    estateOnly: false,
  },
  family_annual: {
    id: 'family_annual',
    name: 'Family plan, paid yearly',
    pricePence: 5999,
    interval: 'year',
    description: `The same as the monthly plan for less: up to ${BILLING_MEMBER_LIMIT} family members and ${BILLING_PRINCIPAL_LIMIT} Principal.`,
    estateOnly: false,
  },
  archive: {
    id: 'archive',
    name: 'Archive plan',
    pricePence: 199,
    interval: 'month',
    description:
      'Keeps the records safe and readable after a death, for the executor and family, at a lower price.',
    estateOnly: true,
  },
};

/** Where the subscription is managed. `trial` = no payment yet; `admin` = set by Seventide support. */
export const BILLING_PROVIDERS = ['trial', 'stripe', 'admin'] as const;
export const BillingProvider = z.enum(BILLING_PROVIDERS);
export type BillingProvider = z.infer<typeof BillingProvider>;

// ---------------------------------------------------------------- cancellation survey (AN-2)

export const CANCELLATION_REASONS = [
  'too_expensive',
  'no_longer_needed',
  'principal_died',
  'missing_features',
  'hard_to_use',
  'switching_service',
  'other',
] as const;
export const CancellationReason = z.enum(CANCELLATION_REASONS);
export type CancellationReason = z.infer<typeof CancellationReason>;

export const CANCELLATION_REASON_LABELS: Record<CancellationReason, string> = {
  too_expensive: 'It costs too much',
  no_longer_needed: 'We no longer need it',
  principal_died: 'The person we were caring for has died',
  missing_features: 'It does not do something we need',
  hard_to_use: 'It is hard to use',
  switching_service: 'We are using something else',
  other: 'Another reason',
};

/**
 * Churn reasons as stored in `subscriptions.cancellation_reason` and analytics `subscription_cancelled.reason`.
 * The survey reasons, plus reasons recorded when a subscription ends outside the survey (Stripe portal,
 * failed payments, admin).
 */
export const CHURN_REASONS = [
  ...CANCELLATION_REASONS,
  'payment_failed',
  'not_given',
] as const;
export type ChurnReason = (typeof CHURN_REASONS)[number];
export const CHURN_REASON_LABELS: Record<ChurnReason, string> = {
  ...CANCELLATION_REASON_LABELS,
  payment_failed: 'Payment failed',
  not_given: 'No reason given',
};

// ---------------------------------------------------------------- workspace endpoints

export const BillingPlanDto = z.object({
  id: BillingPlanId,
  name: z.string(),
  description: z.string(),
  pricePence: z.number().int(),
  interval: z.enum(['month', 'year']),
  /** Offered to this workspace now (archive: estate stage only). */
  available: z.boolean(),
  /** Online payment is switched on (Stripe configured), so this plan can be bought now. */
  purchasable: z.boolean(),
});
export type BillingPlanDto = z.infer<typeof BillingPlanDto>;

/** GET /api/w/:id/platform/billing */
export const BillingOverview = z.object({
  /** Stripe is configured (secret key + webhook secret). */
  enabled: z.boolean(),
  /** The caller may manage billing (owner only, spec §3.2). Others see the status only. */
  canManage: z.boolean(),
  plans: z.array(BillingPlanDto),
  subscription: z.object({
    /** Effective status: an expired trial is reported as `read_only`. */
    status: SubscriptionStatus,
    plan: BillingPlanId.nullable(),
    provider: BillingProvider,
    trialEndsAt: IsoDateTime.nullable(),
    daysLeftInTrial: z.number().int().nullable(),
    currentPeriodEnd: IsoDateTime.nullable(),
    /** Set when the subscription will end at the end of the period (cancelled, not yet ended). */
    cancelAt: IsoDateTime.nullable(),
    readOnly: z.boolean(),
    /** Billing details exist in Stripe, so "Manage billing" (Customer Portal) can open. Owner only. */
    hasBillingAccount: z.boolean(),
  }),
  members: z.object({
    used: z.number().int(),
    pendingInvites: z.number().int(),
    limit: z.number().int(),
  }),
  stage: Stage,
});
export type BillingOverview = z.infer<typeof BillingOverview>;

/** POST /api/w/:id/platform/billing/checkout */
export const BillingCheckoutRequest = z.object({ plan: BillingPlanId });
export type BillingCheckoutRequest = z.infer<typeof BillingCheckoutRequest>;

/** POST /api/w/:id/platform/billing/change-plan — switch an active Stripe subscription to another plan. */
export const BillingChangePlanRequest = z.object({ plan: BillingPlanId });
export type BillingChangePlanRequest = z.infer<typeof BillingChangePlanRequest>;

export const BillingChangePlanResponse = z.object({ ok: z.literal(true), plan: BillingPlanId });
export type BillingChangePlanResponse = z.infer<typeof BillingChangePlanResponse>;

/** Response of checkout and portal: a Stripe-hosted page to redirect to. */
export const BillingRedirectResponse = z.object({ url: z.string().url() });
export type BillingRedirectResponse = z.infer<typeof BillingRedirectResponse>;

/**
 * POST /api/w/:id/platform/billing/cancel — cancel in one step; the survey (AN-2) is optional. Without a reason the
 * churn reason is recorded as `not_given` (free text alone counts as `other`).
 */
export const BillingCancelRequest = z.object({
  reason: CancellationReason.optional(),
  /** Optional free text; stored encrypted, never sent to analytics. */
  details: z.string().trim().max(2000).optional(),
});
export type BillingCancelRequest = z.infer<typeof BillingCancelRequest>;

export const BillingCancelResponse = z.object({
  ok: z.literal(true),
  /** When access ends (end of the paid period), or null if unknown. */
  cancelAt: IsoDateTime.nullable(),
});
export type BillingCancelResponse = z.infer<typeof BillingCancelResponse>;

// ---------------------------------------------------------------- admin (/api/admin/billing)

export const AdminBillingSearchQuery = z.object({
  /** Workspace id (exact) or part of the workspace name. Principal names are encrypted and never searched. */
  q: z.string().trim().max(100).optional(),
  status: SubscriptionStatus.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  offset: z.coerce.number().int().min(0).default(0),
});
export type AdminBillingSearchQuery = z.infer<typeof AdminBillingSearchQuery>;

export const AdminBillingWorkspace = z.object({
  id: Id,
  name: z.string(),
  createdAt: IsoDateTime,
  stage: Stage,
  nation: Nation,
  memberCount: z.number().int(),
  subscription: z.object({
    status: SubscriptionStatus,
    /** Stored status before the expired-trial rule is applied. */
    storedStatus: SubscriptionStatus,
    plan: z.string().nullable(),
    provider: BillingProvider,
    trialEndsAt: IsoDateTime.nullable(),
    currentPeriodEnd: IsoDateTime.nullable(),
    cancelAt: IsoDateTime.nullable(),
    cancellationReason: z.string().nullable(),
    readOnly: z.boolean(),
    hasStripeCustomer: z.boolean(),
    hasStripeSubscription: z.boolean(),
    updatedAt: IsoDateTime.nullable(),
  }),
});
export type AdminBillingWorkspace = z.infer<typeof AdminBillingWorkspace>;

export const AdminBillingList = z.object({
  items: z.array(AdminBillingWorkspace),
  total: z.number().int(),
  limit: z.number().int(),
  offset: z.number().int(),
  billingEnabled: z.boolean(),
});
export type AdminBillingList = z.infer<typeof AdminBillingList>;

/** A date (`yyyy-mm-dd`, treated as the end of that UK day) or a full ISO timestamp. */
export const AdminBillingDate = z.union([IsoDate, IsoDateTime]);

/** PATCH /api/admin/billing/workspaces/:workspaceId — manual override (provider becomes `admin`). */
export const AdminBillingUpdateRequest = z
  .object({
    status: SubscriptionStatus.optional(),
    plan: BillingPlanId.nullable().optional(),
    trialEndsAt: AdminBillingDate.nullable().optional(),
    currentPeriodEnd: AdminBillingDate.nullable().optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), {
    message: 'Change at least one field.',
  });
export type AdminBillingUpdateRequest = z.infer<typeof AdminBillingUpdateRequest>;

export const AdminBillingWorkspaceParams = z.object({ workspaceId: Id });
